import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { setSubscriptionTopicCache } from './cache';
import { parseSubscriptionTopic, subscriptionTopicKeys, type SubscriptionTopic } from './entity';

/**
 * Topics for the workspace, in server order.
 *
 * `staleTime: 0`: the subscription center reads topics on the index, the
 * branding preview and the localization editor, and every one of those has to
 * agree with the last write — a stale list shows the wrong "missing
 * translations" badge and the wrong delete guard.
 *
 * @public
 */
export async function getSubscriptionTopics(workspaceId: number, qc: QueryClient): Promise<SubscriptionTopic[]> {
  const data = await hydraUiApi.subscriptionCenter.getSubscriptionTopics({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const topics = (data.subscription_topics ?? []).map(parseSubscriptionTopic);
  for (const topic of topics) setSubscriptionTopicCache(qc, workspaceId, topic);
  return topics;
}

/** @public */
export const getSubscriptionTopicsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: subscriptionTopicKeys.list(workspaceId, undefined),
    queryFn: () => getSubscriptionTopics(workspaceId, qc),
    staleTime: 0,
  });

interface UseSubscriptionTopicsOptions {
  queryConfig?: QueryConfig<typeof getSubscriptionTopicsQueryOptions>;
}

/** @public */
export const useSubscriptionTopics = ({ queryConfig }: UseSubscriptionTopicsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getSubscriptionTopicsQueryOptions(workspaceId, qc), ...queryConfig });
};

/** @public */
export async function getSubscriptionTopic(workspaceId: number, id: string): Promise<SubscriptionTopic> {
  const data = await hydraUiApi.subscriptionCenter.getSubscriptionTopicsByTopic({
    params: { path: { environment_id: String(workspaceId), topic_id: id } },
  });
  if (data.subscription_topic == null) {
    throw new Error(`Subscription topic ${id} was not found.`);
  }
  return parseSubscriptionTopic(data.subscription_topic);
}

/** @public */
export const getSubscriptionTopicQueryOptions = (workspaceId: number, id: string) =>
  queryOptions({
    queryKey: subscriptionTopicKeys.detail(workspaceId, id),
    queryFn: () => getSubscriptionTopic(workspaceId, id),
    staleTime: 0,
  });

interface UseSubscriptionTopicOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getSubscriptionTopicQueryOptions>;
}

/** @public */
export const useSubscriptionTopic = ({ id, queryConfig }: UseSubscriptionTopicOptions) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({ ...getSubscriptionTopicQueryOptions(workspaceId, id), ...queryConfig });
};
